import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Container from 'common/Container';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Link from '@material-ui/core/Link';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { CHERRY_SOFTWARE_CONTAINER_PROPS } from './cherrySoftwareLayout';
import { CherryDevTheme } from './CherryDevTheme';
import {
  crmHealth,
  fetchCherrySoftwareLeads,
  getCrmAdminKey,
  persistCrmAdminKey,
  updateCherrySoftwareLead,
  deleteCherrySoftwareLead,
  crmServiceBase,
} from 'services/cherrySoftwareLeadService';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'nuevo', label: 'Nuevo' },
  { value: 'contactado', label: 'Contactado' },
  { value: 'calificado', label: 'Calificado' },
  { value: 'cerrado', label: 'Cerrado' },
];

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-VE', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return String(value);
  }
};

const CherrySoftwareCrm = () => {
  const theme = useTheme();
  const [adminKeyInput, setAdminKeyInput] = useState(getCrmAdminKey());
  const [authenticated, setAuthenticated] = useState(Boolean(getCrmAdminKey()));
  const [statusFilter, setStatusFilter] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);
  const [editingNotes, setEditingNotes] = useState({});
  const [deletingId, setDeletingId] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null);

  useEffect(() => {
    document.title = 'CRM cherryDev';
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const h = await crmHealth();
      setHealth(h);
    } catch {
      setHealth({ ok: false });
    }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchCherrySoftwareLeads(statusFilter);
      setLeads(res.leads || []);
      setAuthenticated(true);
    } catch (e) {
      setError(e.message || 'Error al cargar leads');
      if (String(e.message).toLowerCase().includes('clave')) {
        setAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    if (authenticated) loadLeads();
  }, [authenticated, loadLeads]);

  const handleLogin = (event) => {
    event.preventDefault();
    persistCrmAdminKey(adminKeyInput);
    setAuthenticated(Boolean(adminKeyInput.trim()));
    setError('');
  };

  const handleLogout = () => {
    persistCrmAdminKey('');
    setAuthenticated(false);
    setLeads([]);
    setAdminKeyInput('');
  };

  const handleStatusChange = async (leadId, status) => {
    const previous = leads.find((l) => l._id === leadId);
    if (!previous) return;

    setLeads((prev) => {
      if (statusFilter && status !== statusFilter) {
        return prev.filter((l) => l._id !== leadId);
      }
      return prev.map((l) => (l._id === leadId ? { ...l, status } : l));
    });
    setError('');
    try {
      await updateCherrySoftwareLead(leadId, { status });
    } catch (e) {
      setLeads((prev) => {
        if (prev.some((l) => l._id === leadId)) {
          return prev.map((l) => (l._id === leadId ? previous : l));
        }
        return [previous, ...prev].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      });
      setError(e.message);
    }
  };

  const handleSaveNotes = async (leadId) => {
    const notes = editingNotes[leadId] ?? leads.find((l) => l._id === leadId)?.notes ?? '';
    const previous = leads.find((l) => l._id === leadId)?.notes ?? '';
    if (notes === previous) {
      setEditingNotes((prev) => {
        const next = { ...prev };
        delete next[leadId];
        return next;
      });
      return;
    }
    setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, notes } : l)));
    setError('');
    try {
      await updateCherrySoftwareLead(leadId, { notes });
      setEditingNotes((prev) => {
        const next = { ...prev };
        delete next[leadId];
        return next;
      });
    } catch (e) {
      setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, notes: previous } : l)));
      setError(e.message);
    }
  };

  const performDeleteLead = async (lead, deleteTrelloCard) => {
    setDeletingId(lead._id);
    setError('');
    const snapshot = leads;
    setLeads((prev) => prev.filter((l) => l._id !== lead._id));
    setEditingNotes((prev) => {
      const next = { ...prev };
      delete next[lead._id];
      return next;
    });
    try {
      await deleteCherrySoftwareLead(lead._id, { deleteTrelloCard });
    } catch (e) {
      setLeads(snapshot);
      setError(e.message || 'No se pudo eliminar el lead');
    } finally {
      setDeletingId('');
    }
  };

  const openDeleteDialog = (lead) => {
    setDeleteDialog({ lead, step: 'crm' });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog(null);
  };

  const handleDeleteDialogNo = () => {
    if (!deleteDialog) return;
    if (deleteDialog.step === 'trello') {
      closeDeleteDialog();
      performDeleteLead(deleteDialog.lead, false);
      return;
    }
    closeDeleteDialog();
  };

  const handleDeleteDialogYes = () => {
    if (!deleteDialog) return;
    const { lead, step } = deleteDialog;
    const hasTrelloCard = Boolean(lead.trelloCardId || lead.trelloCardUrl);

    if (step === 'crm') {
      if (hasTrelloCard) {
        setDeleteDialog({ lead, step: 'trello' });
        return;
      }
      closeDeleteDialog();
      performDeleteLead(lead, false);
      return;
    }

    closeDeleteDialog();
    performDeleteLead(lead, true);
  };

  const deleteDialogLabel =
    deleteDialog?.lead?.fullName || deleteDialog?.lead?.email || 'este lead';

  if (!authenticated) {
    return (
      <Box minHeight="70vh" display="flex" alignItems="center" py={8}>
        <Container maxWidth={480} width="100%">
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 800 }}>
            CRM cherryDev
          </Typography>
          <Typography color="textSecondary" paragraph>
            Consulta los leads de la landing cherryDev. Usa la clave{' '}
            <code>CHERRY_SOFTWARE_ADMIN_API_KEYS</code> del servicio CRM.
          </Typography>
          <Box component="form" onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Clave admin (X-Cherry-Software-Admin-Key)"
              type="password"
              value={adminKeyInput}
              onChange={(e) => setAdminKeyInput(e.target.value)}
              margin="normal"
              required
            />
            <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }}>
              Entrar
            </Button>
          </Box>
          {error ? (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          ) : null}
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 3 }}>
            API: {crmServiceBase() || '(configura REACT_APP_CRM_SERVICE_URL)'}
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box py={{ xs: 4, md: 6 }} bgcolor={theme.palette.background.paper}>
      <Container {...CHERRY_SOFTWARE_CONTAINER_PROPS}>
        <Grid container spacing={2} alignItems="center" mb={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              CRM cherryDev
            </Typography>
            <Typography color="textSecondary" variant="body2">
              Leads en MongoDB · sincronizados con Trello (tablero cherryDev)
              {health?.cherrySoftwareTrelloConfigured ? ' ✓' : ' (Trello Cherry no configurado)'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={1} justifyContent={{ md: 'flex-end' }} flexWrap="wrap">
              <TextField
                select
                size="small"
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="outlined" onClick={loadLeads} disabled={loading}>
                Actualizar
              </Button>
              <Button variant="text" onClick={handleLogout}>
                Salir
              </Button>
            </Box>
          </Grid>
        </Grid>

        {error ? (
          <Typography color="error" paragraph>
            {error}
          </Typography>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Contacto</TableCell>
                  <TableCell>Sistema</TableCell>
                  <TableCell>Presupuesto</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Trello</TableCell>
                  <TableCell>Notas</TableCell>
                  <TableCell align="center" padding="checkbox">
                    Eliminar
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No hay leads{statusFilter ? ` con estado "${statusFilter}"` : ''}.
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => (
                    <TableRow key={lead._id} hover>
                      <TableCell>{formatDate(lead.createdAt)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {lead.fullName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {lead.email}
                        </Typography>
                        {lead.website ? (
                          <Typography variant="caption" display="block">
                            {lead.website}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{lead.systemTypeLabel}</TableCell>
                      <TableCell>{lead.budgetLabel}</TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={lead.status}
                          onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                          sx={{ minWidth: 130 }}
                        >
                          {STATUS_OPTIONS.filter((o) => o.value).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {lead.trelloCardUrl ? (
                          <Link href={lead.trelloCardUrl} target="_blank" rel="noopener noreferrer">
                            Ver tarjeta
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={1}
                          value={editingNotes[lead._id] ?? lead.notes ?? ''}
                          onChange={(e) =>
                            setEditingNotes((prev) => ({ ...prev, [lead._id]: e.target.value }))
                          }
                          onBlur={() => handleSaveNotes(lead._id)}
                          placeholder="Notas internas"
                        />
                      </TableCell>
                      <TableCell align="center" padding="checkbox">
                        <Tooltip title="Eliminar lead">
                          <span>
                            <IconButton
                              size="small"
                              color="secondary"
                              aria-label={`Eliminar ${lead.fullName}`}
                              onClick={() => openDeleteDialog(lead)}
                              disabled={deletingId === lead._id}
                            >
                              {deletingId === lead._id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Container>

      <Dialog open={Boolean(deleteDialog)} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {deleteDialog?.step === 'trello' ? 'Tarjeta en Trello' : 'Eliminar lead'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {deleteDialog?.step === 'trello' ? (
              <>
                ¿Eliminar también la tarjeta de Trello de <strong>{deleteDialogLabel}</strong>?
              </>
            ) : (
              <>
                ¿Eliminar a <strong>{deleteDialogLabel}</strong> del CRM?
              </>
            )}
          </Typography>
          {deleteDialog?.step === 'trello' ? (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1.5 }}>
              No = el lead se borra del CRM y la tarjeta permanece en Trello.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogNo} variant="outlined">
            No
          </Button>
          <Button onClick={handleDeleteDialogYes} color="secondary" variant="contained">
            Sí
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const CherrySoftwareCrmPage = () => (
  <CherryDevTheme>
    <CherrySoftwareCrm />
  </CherryDevTheme>
);

export default CherrySoftwareCrmPage;
